import type { CSSProperties } from '@mantine/core'
import type { OrderDetails } from '../../model'
import type { GroupOrderCardCompactProps } from './GroupOrderCardCompact.types'
import { Button, Text } from '@mantine/core'
import { clsx } from 'clsx'
import { extractRgbValues } from '@/shared/lib/mantine/mantine.utils'
import { OrderItemWithMetaInfoCompact } from '../order-item-with-meta-info-compact'
import classes from './GroupOrderCardCompact.module.css'

export function GroupOrderCardCompact({
  orders,
  cardTitle,
  onClick,
  onOrderItemClick,
  onAction,
  actionText,
  className,
  animate,
  outline,
  color,
  statusColor,
  renderStatus,
  secondaryText,
  dataOnboarding,
}: GroupOrderCardCompactProps) {
  const pulseStyle = (): CSSProperties | undefined => {
    const effectiveColor = color || statusColor

    if (!effectiveColor || (!animate && !outline))
      return

    const _pulseColorRGBA = `rgba(${extractRgbValues(effectiveColor)}, 0.7)`
    const _pulseColorRGBValues = extractRgbValues(effectiveColor, false)

    return {
      '--status-color': _pulseColorRGBA,
      '--pulse-color': _pulseColorRGBValues,
    } as CSSProperties
  }

  function handleCardClick() {
    if (onClick !== undefined) {
      onClick()
    }
  }

  function handleOrderItemClick(e: React.MouseEvent, orderId: OrderDetails['orderId']) {
    e.stopPropagation()

    if (onOrderItemClick !== undefined) {
      onOrderItemClick(orderId)
    }
  }

  function handleActionClick(e: React.MouseEvent) {
    e.stopPropagation()

    if (onAction !== undefined) {
      onAction()
    }
  }

  return (
    <div
      className={clsx({
        [classes.groupOrderCardCompact]: true,
        [classes.cardOutline]: (animate || outline) && (color || statusColor),
        [classes.cardAnimationPulse]: animate && (color || statusColor),
        [className as string]: !!className,
      })}
      style={pulseStyle()}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      data-onboarding={dataOnboarding}
    >
      <div className={classes.orderContent}>
        <div className={classes.orderDetailsContainer}>
          <div className={classes.orderDetails}>
            {cardTitle && (
              <Text
                className={classes.cardTitle}
              >
                {cardTitle}
              </Text>
            )}
            {renderStatus ? (
              renderStatus()
            ) : statusColor ? (
              <Text
                className={classes.statusTextWithIndicator}
                style={{ '--indicator-color': statusColor } as React.CSSProperties}
              >
                Partner Orders
              </Text>
            ) : null}
            {secondaryText && (
              <Text
                className={classes.cardSubtitle}
                size="xs"
                c="dimmed"
              >
                {secondaryText}
              </Text>
            )}
          </div>
        </div>

        <div className={classes.orderItemList}>
          {orders.map((order) => {
            return (
              <OrderItemWithMetaInfoCompact
                orderId={order.orderId}
                bg="gray.1"
                key={order.orderId}
                customerName={order.customerName}
                onClick={handleOrderItemClick}
              />
            )
          })}
        </div>
      </div>

      {actionText && onAction && (
        <Button
          color={color || statusColor || 'violet'}
          onClick={handleActionClick}
          variant="outline"
          w="100%"
          mt="auto"
        >
          {actionText}
        </Button>
      )}
    </div>
  )
}
